import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalFuncProps, UploadFile } from 'antd';
import useApi from '../hooks/useApi';
import useIsPatient from '../hooks/useIsPatient';
import { useQuery } from '@tanstack/react-query';
import { getMetaMiscQuery, MiscMeta } from '../schemas/getMetaMisc';
import { getFileExtension } from 'vl-common/src/utils';

enum FileStatus {
  Done = 'done',
  Error = 'error',
  Uploading = 'uploading'
}

const SUPPORT_24_HR_PHONE = '0115 8573843';
const SUPPORT_EMAIL = 'support@hbsuk.co.uk';

type FileTypeCode = MiscMeta['file_types'][number]['file_type_code'];

const toBase64 = (file: UploadFile['originFileObj']) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file as Blob);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const uploadBlobToS3Url = (blob: Blob, url: string, mime: string) => {
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': mime,
      redirect: 'follow'
    },
    body: blob
  });
};

const blockedFileTypes = [
  'ace',
  'ade',
  'adp',
  'ani',
  'app',
  'appcontent-ms',
  'appref-ms',
  'asp',
  'aspx',
  'asx',
  'bas',
  'bat',
  'cdxml',
  'cer',
  'chm',
  'cmd',
  'cnt',
  'com',
  'cpl',
  'crt',
  'csh',
  'der',
  'diagcab',
  'docm',
  'exe',
  'fxp',
  'gadget',
  'grp',
  'hlp',
  'hpj',
  'hta',
  'htc',
  'inf',
  'ins',
  'drawio',
  'isp',
  'its',
  'jar',
  'jnlp',
  'js',
  'jse',
  'ksh',
  'lnk',
  'mad',
  'maf',
  'mag',
  'mam',
  'maq',
  'mar',
  'mas',
  'mat',
  'mau',
  'mav',
  'maw',
  'mcf',
  'mda',
  'mdb',
  'mde',
  'mdt',
  'mdw',
  'mdz',
  'mht',
  'mhtml',
  'msc',
  'msh',
  'msh1',
  'msh1xml',
  'msh2',
  'msh2xml',
  'mshxml',
  'msi',
  'msp',
  'mst',
  'msu',
  'ops',
  'osd',
  'pcd',
  'pif',
  'pl',
  'plg',
  'prf',
  'prg',
  'printerexport',
  'ps1',
  'ps1.xml',
  'ps2',
  'ps2xml',
  'psc1',
  'psc2',
  'psd1',
  'psdm1',
  'psdm1cdxml',
  'pssc',
  'pst',
  'py',
  'pyc',
  'pyo',
  'pyw',
  'pyz',
  'pyzw',
  'reg',
  'scf',
  'scr',
  'sct',
  'settingcontent-ms',
  'shb',
  'shs',
  'theme',
  'tmp',
  'udl',
  'url',
  'vb',
  'vbe',
  'vbp',
  'vbs',
  'vsmacros',
  'vss',
  'vst',
  'vsw',
  'webpnp',
  'website',
  'ws',
  'wsb',
  'wsc',
  'wsf',
  'wsh',
  'xbap',
  'xll',
  'xml',
  'xnk',
  'zip'
];

export default function useUploadDocument({
  userId,
  caseId,
  appId,
  isPatientDocsTabUpload = false,
  afterFileUpload = () => {}
}: {
  userId?: string;
  caseId?: string;
  appId?: string;
  isPatientDocsTabUpload?: boolean;
  afterFileUpload?: () => void;
}) {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const isPatient = useIsPatient();

  const [files, setFiles] = useState<UploadFile[]>([]);
  const [modals, setModals] = useState<ModalFuncProps[]>([]);
  const [selectedFileType, setSelectedFileType] = useState<FileTypeCode | null>(null);

  const { data: meta } = useQuery({ ...getMetaMiscQuery() });

  const uploadDocumentOptions = meta?.file_types.map((file) => ({
    value: file.file_type_code,
    label: file.file_type_desc
  }));

  const onAddModal = (modal: ModalFuncProps) => {
    setModals((modals) => [...modals, modal]);
  };

  const onRemoveModal = () => {
    setModals((modals) => modals.slice(1));
  };

  const removeFileFromFilesList = (file: UploadFile) =>
    setFiles((files) => {
      return files.filter(({ uid }) => uid !== file.uid);
    });

  const updateFileInFilesList = (updatedFile: UploadFile) =>
    setFiles((files) => {
      return files.map((file) => (file.uid === updatedFile.uid ? updatedFile : file));
    });

  const handleUploadError = (errMessage: string, filename: string) => {
    const isDocxError = filename.split('.').pop() === 'docx';

    onAddModal({
      type: 'error',
      title: 'Upload Error',
      content: (
        <>
          {t('An error has occurred while trying to upload file')} <b>{filename}</b>:
          <br />
          <br />
          {errMessage}
          {isDocxError && (
            <>
              <br />
              <br />
              {t('If you are having trouble uploading your .docx file, try converting it to a PDF')}
            </>
          )}
        </>
      ),
      okText: 'OK',
      onOk: onRemoveModal
    });
  };

  const continueUploadDoc = async (file: UploadFile, filename: string, base64: string) => {
    try {
      file.percent = 30;
      updateFileInFilesList(file);

      await Api.file.uploadDocsToDatabase({
        user_guid: userId,
        filename,
        appt_id: appId,
        case_id: caseId,
        file_type: selectedFileType,
        ...(isPatientDocsTabUpload && { docs_tab: true })
      });

      file.percent = 50;
      updateFileInFilesList(file);

      const fileEtension = getFileExtension(filename);

      const { data } = await Api.file.generatePreSignedS3Url({
        user_guid: userId,
        filename,
        appt_id: appId,
        base64: fileEtension === 'docx' ? base64 : base64.substring(0, 100)
      });

      if (data?.errorMessage) {
        handleUploadError(data?.errorMessage, filename);
        return;
      }

      const { mime, url } = data;
      const blob = await (await fetch(`data:${mime};base64,${base64}`)).blob();
      await uploadBlobToS3Url(blob, url, mime);

      file.percent = 70;
      updateFileInFilesList(file);

      await Api.file.fileHasBeenUploaded({
        user_guid: userId,
        filename,
        appt_id: appId,
        case_id: caseId
      });

      file.percent = 90;
      updateFileInFilesList(file);

      file.status = FileStatus.Done;
      afterFileUpload();
      onAddModal({
        type: 'success',
        title: 'Upload Successful',
        content: (
          <>
            <p>
              {t('The file')} <b>{filename}</b> {t('has been successfully uploaded.')}
            </p>
            <p>
              {t(
                'If this document is of an urgent nature or requires further action, please contact our Customer Services team on '
              )}
              <a style={{ textDecoration: 'underline' }} href={`tel:${SUPPORT_24_HR_PHONE.trim()}`}>
                {SUPPORT_24_HR_PHONE}
              </a>
              {t(' or alternatively ')}
              <a style={{ textDecoration: 'underline' }} href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              {t(', where a member of our team will be happy to assist with your enquiry.')}
            </p>
          </>
        ),
        okText: 'OK',
        onOk: onRemoveModal
      });
    } catch (err) {
      // @ts-ignore
      const errorData = (await err?.response?.json().catch(() => null)) || err?.response?.data || {};

      const errMessage = errorData.errorMessage || errorData.message || 'File upload failed';

      const messageWithoutErrorNumber = errMessage.replace(/^\d+\s*[-:]?\s*/, '');

      handleUploadError(messageWithoutErrorNumber, filename);
    } finally {
      removeFileFromFilesList(file);
    }
  };

  const validateDoc = async (file: UploadFile, filename: string, base64: string) => {
    try {
      await Api.file.pdfValidate({
        pat_guid: userId,
        pdfFilename: filename,
        base64
      });

      file.percent = 20;
      updateFileInFilesList(file);

      await continueUploadDoc(file, filename, base64);
    } catch (err) {
      // @ts-ignore
      const message = err?.response?.data?.status;
      onAddModal({
        type: 'confirm',
        title: 'Validate Warning',
        content: (
          <>
            {message}
            <br />
            <br />
            {t('Are you sure you want to upload this file:')} <b>{filename}</b>?
          </>
        ),
        closable: true,
        okText: 'Upload',
        cancelText: 'Cancel',
        onOk: () => {
          continueUploadDoc(file, filename, base64).then();
          onRemoveModal();
        },
        onCancel: () => {
          removeFileFromFilesList(file);
          onRemoveModal();
        }
      });
    }
  };

  const uploadDoc = async (file: UploadFile) => {
    const filename = (file.originFileObj as UploadFile).name;
    try {
      if (!appId && !caseId) {
        throw Error('Something went wrong');
      }

      const fileNameArr = filename.split('.');
      const fileType = fileNameArr[fileNameArr.length - 1];
      const regex = /[~"`%#&*<>?/\\{|}]+/;

      if (blockedFileTypes.includes(fileType)) {
        throw Error('Unsupported file type');
      }

      if (file.size && file.size > 10485760) {
        throw Error('File size exceeds 10MB');
      }

      if (fileNameArr.some((fileName) => regex.test(fileName))) {
        throw Error('File name contains invalid characters such as: #, %, &, etc.');
      }

      file.status = FileStatus.Uploading;
      file.percent = 10;
      updateFileInFilesList(file);

      const [, base64] = (await toBase64(file.originFileObj)).split(',');

      if (!isPatient && fileType === 'pdf') {
        await validateDoc(file, filename, base64);
        return;
      }

      await continueUploadDoc(file, filename, base64);
    } catch (err) {
      // @ts-ignore
      const message = err?.message;

      onAddModal({
        type: 'error',
        title: 'Upload Error',
        content: (
          <>
            {t('An error has occurred while trying to upload file')} <b>{filename}</b>:
            <br />
            <br />
            {message}
          </>
        ),
        okText: 'OK',
        onOk: onRemoveModal
      });

      removeFileFromFilesList(file);
    }
  };

  const uploadDocs = async (files: UploadFile[]) => {
    return Promise.all(files.map((file) => uploadDoc(file)));
  };

  return {
    files,
    setFiles,
    uploadDoc,
    uploadDocs,
    fileTypes: uploadDocumentOptions,
    setSelectedFileType,
    selectedFileType,
    modal: modals[0]
  };
}
